import styles from './HomeStaticBlock.module.scss';
import products from '../../../../assets/images/home/products.png';
import product1 from '../../../../assets/images/home/product1.png';
import product2 from '../../../../assets/images/home/product2.png';
import product3 from '../../../../assets/images/home/product3.png';

import AppPrivataSidebarIcon from 'Containers/Common/AppPrivataSidebarIcon';
import { ReactComponent as Shop } from '../../../../assets/svg/icons/shop.svg';

import { Button } from '@mui/material';
import useWindowSize from '../../../../utils/useWindowSize';
import clx from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getActiveSalonId } from 'Containers/Auth/store/selectors';
import { isAmbassadorPromoExcluded } from '../../utils/ambassadorForm';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { Package, Store, ChevronRight, Check, Sparkles } from 'lucide-react';
import WhatsNewModal from '../WhatsNewModal';
import AmbassadorPromoCard from '../AmbassadorPromoCard';
import {
  HOME_AMBASSADOR_CARD_DISMISSED_KEY,
  HOME_PRIVATE_APP_PROMO_DISMISSED_KEY,
  HOME_WHATS_NEW_BANNER_DISMISSED_KEY,
  HOME_WHATS_NEW_MODAL_DISMISSED_KEY,
  readLocalFlag,
  writeLocalFlag,
} from '../../utils/homePromoStorage';
import { isLightClient } from 'utils/lowMemory';

const isStaffAccount = () => {
  try {
    return Boolean(localStorage.getItem('staff'));
  } catch {
    return false;
  }
};

const HomeStaticBlock = ({
  showPrivateApp = false,
  showShop = false,
  showWhatsNew = false,
  showAmbassador = false,
}) => {
  const { isMobile } = useWindowSize();
  const activeSalonId = useSelector(getActiveSalonId());
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [privateAppDismissed, setPrivateAppDismissed] = useState(() =>
    readLocalFlag(HOME_PRIVATE_APP_PROMO_DISMISSED_KEY),
  );
  const [whatsNewBannerDismissed, setWhatsNewBannerDismissed] = useState(() =>
    readLocalFlag(HOME_WHATS_NEW_BANNER_DISMISSED_KEY),
  );
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [ambassadorDismissed, setAmbassadorDismissed] = useState(() =>
    readLocalFlag(HOME_AMBASSADOR_CARD_DISMISSED_KEY),
  );

  const dismissPrivateApp = useCallback(() => {
    writeLocalFlag(HOME_PRIVATE_APP_PROMO_DISMISSED_KEY);
    setPrivateAppDismissed(true);
  }, []);

  /** "Non mostrare più" sul banner: sparisce banner + modal per sempre (fino a bump versione). */
  const dismissWhatsNewBanner = useCallback(() => {
    writeLocalFlag(HOME_WHATS_NEW_BANNER_DISMISSED_KEY);
    writeLocalFlag(HOME_WHATS_NEW_MODAL_DISMISSED_KEY);
    setWhatsNewBannerDismissed(true);
    setWhatsNewOpen(false);
  }, []);

  /** Qualsiasi chiusura (X, overlay, "Ho capito"): il modal non si riapre da solo, il banner resta. */
  const closeWhatsNew = useCallback(() => {
    writeLocalFlag(HOME_WHATS_NEW_MODAL_DISMISSED_KEY);
    setWhatsNewOpen(false);
  }, []);

  const showPrivateAppBanner = showPrivateApp && !privateAppDismissed;
  // Gli account staff non vedono né banner né modal Novità, né la promo Ambassador.
  const staffAccount = isStaffAccount();
  const showWhatsNewBanner = showWhatsNew && !whatsNewBannerDismissed && !staffAccount;
  const showAmbassadorCard =
    showAmbassador &&
    !ambassadorDismissed &&
    !staffAccount &&
    !isAmbassadorPromoExcluded(activeSalonId);

  useEffect(() => {
    if (!showWhatsNewBanner) return undefined;
    if (isLightClient()) return undefined;
    if (readLocalFlag(HOME_WHATS_NEW_MODAL_DISMISSED_KEY)) return undefined;
    const timer = window.setTimeout(() => setWhatsNewOpen(true), 600);
    return () => window.clearTimeout(timer);
  }, [showWhatsNewBanner]);

  if (!showPrivateAppBanner && !showShop && !showWhatsNewBanner && !showAmbassadorCard) return null;

  return (
    <div className={clx(styles.cards_container, { [styles.cards_container_mobile]: isMobile })}>
      {showAmbassadorCard && (
        <AmbassadorPromoCard onDismiss={() => setAmbassadorDismissed(true)} />
      )}
      {showWhatsNewBanner && (
        <div className={styles.whats_new_section}>
          <div className={styles.whats_new_info}>
            <div className={styles.info_block}>
              <span className={styles.whats_new_badge}>{t('home.staticBlock.whatsNew.badge')}</span>
              <div className={styles.whats_new_title_row}>
                <span className={styles.whats_new_logo} aria-hidden>
                  <Sparkles size={22} strokeWidth={2.2} />
                </span>
                <div className={styles.info_title}>{t('home.staticBlock.whatsNew.title')}</div>
              </div>
              <p className={styles.whats_new_subtitle}>{t('home.staticBlock.whatsNew.subtitle')}</p>
            </div>
            <div className={styles.whats_new_actions}>
              <Button onClick={() => setWhatsNewOpen(true)} className={styles.whats_new_button}>
                {t('home.staticBlock.whatsNew.cta')}
              </Button>
              <button
                type="button"
                className={styles.whats_new_dismiss}
                onClick={dismissWhatsNewBanner}
              >
                {t('home.staticBlock.whatsNew.dismissBanner')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrivateAppBanner && (
        <div className={styles.private_app_section}>
          <div className={styles.private_app_info}>
            <div className={styles.info_block}>
              <span className={styles.promo_badge}>{t('home.staticBlock.privateApp.badge')}</span>
              <div className={styles.private_app_title_row}>
                <span className={styles.private_app_logo} aria-hidden>
                  <AppPrivataSidebarIcon />
                </span>
                <div className={styles.info_title}>{t('home.staticBlock.privateApp.title')}</div>
              </div>
            </div>
            <div className={styles.whats_new_actions}>
              <Button
                onClick={() => navigate(ROUTES.PRIVATE_APP)}
                className={styles.private_app_button}
              >
                {t('home.staticBlock.privateApp.cta')}
              </Button>
              <button
                type="button"
                className={styles.whats_new_dismiss}
                onClick={dismissPrivateApp}
              >
                {t('home.staticBlock.whatsNew.dismissBanner')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShop && (
        <div className={styles.shop_section}>
          <div className={styles.mobile_img_block}>
            <img src={product1} alt='' />
            <img src={product2} alt='' />
            <img src={product3} alt='' />
          </div>
          <div className={styles.shop_info}>
            <div className={styles.shop_heading}>
              <span className={styles.shop_icon} aria-hidden>
                <Shop />
              </span>
              <div className={styles.shop_heading_copy}>
                <span className={styles.shop_badge}>
                  <Check size={12} strokeWidth={2.6} />
                  {t('home.staticBlock.shop.badge')}
                </span>
                <div className={styles.info_title}>{t('home.staticBlock.shop.title')}</div>
              </div>
            </div>
            <ul className={styles.shop_list}>
              {(t('home.staticBlock.shop.items', { returnObjects: true }) || []).map((item, index) => {
                const StepIcon = index === 1 ? Store : Package;
                return (
                  <li key={item}>
                    <span className={styles.shop_step_icon} aria-hidden>
                      <StepIcon size={15} strokeWidth={2.2} />
                    </span>
                    <span>{item}</span>
                  </li>
                );
              })}
            </ul>
            <div className={styles.shop_tags}>
              <span>{t('home.staticBlock.shop.showcase')}</span>
              <span>{t('home.staticBlock.shop.warehouse')}</span>
            </div>
            <Button onClick={() => navigate(ROUTES.SHOP)} className={styles.shop_button}>
              {t('home.staticBlock.shop.cta')}
              <ChevronRight size={16} strokeWidth={2.4} />
            </Button>
          </div>
          <div className={styles.shop_media}>
            <img
              className={styles.shop_img}
              src={products}
              alt={t('home.staticBlock.shop.title')}
            />
          </div>
        </div>
      )}

      {showWhatsNewBanner && (
        <WhatsNewModal isOpen={whatsNewOpen} onClose={closeWhatsNew} />
      )}
    </div>
  );
};

export default HomeStaticBlock;
